import React from 'react';

import PropTypes from 'prop-types';

import { ClockIcon } from '@adsk/alloy-react-icon';
import theme, {
  getInteractiveStyles,
  stylePropType,
  StylableComponent,
} from '@adsk/alloy-react-theme';

import { disabledStyle } from './consts';
import useTimePickerControl, {
  UseTimePickerControlProps,
} from './hooks/useTimePickerControl';
import AmPmInput from './layout/AmPmInput';
import NumberInput from './layout/NumberInput';

type Props = Omit<StylableComponent<HTMLDivElement>, 'onChange'> &
  UseTimePickerControlProps & {
    error?: boolean;
  };

/**
 * A TimePicker
 * Time pickers allow the selection of specific time. It displays a default text field where user can adjust hours, minutes and format.
 */
const TimePickerControl = React.forwardRef<HTMLDivElement, Props>(
  (
    {
      className,
      style,
      onChange,
      onEditDone,
      initialTime,
      error = false,
      disabled = false,
      is12Hour,
      ...props
    },
    ref,
  ) => {
    const {
      getHourInputProps,
      getMinuteInputProps,
      showTimePeriod,
      getTimePeriodInputProps,
    } = useTimePickerControl({
      disabled,
      is12Hour,
      initialTime,
      onChange,
      onEditDone,
    });

    return (
      <div
        {...props}
        ref={ref}
        className={className}
        css={[
          {
            display: 'flex',
            alignItems: 'center',
            height: 34,
            color: theme.tokens.colors.text.primary.value,
            border: `1px solid ${theme.tokens.colors.border.value}`,
            boxSizing: 'border-box',
            borderRadius: theme.borderRadius.small,
            paddingRight: 7,
            paddingLeft: 11,
            ...(disabled && getInteractiveStyles(disabled)),
            ...(disabled && disabledStyle),
            ...(error && {
              borderColor: theme.tokens.colors.border.error.value,
            }),
            userSelect: 'none',
          },
          theme.typography.labelMedium,
          style,
        ]}
      >
        <ClockIcon
          size={20}
          style={{
            marginRight: 4,
            color: disabled
              ? theme.tokens.colors.icon.inactive.value
              : theme.tokens.colors.icon.secondary.value,
          }}
        />
        <div
          ref={ref}
          css={[
            { display: 'flex', alignItems: 'center', marginRight: 4 },
            style,
          ]}
          {...props}
        >
          <NumberInput {...getHourInputProps()} />
          <div
            css={[
              {
                width: 8,
                height: 24,
                display: 'flex',
                justifyContent: 'center',
                alignContent: 'center',
              },
            ]}
          >
            :
          </div>
          <NumberInput {...getMinuteInputProps()} />
          {showTimePeriod && <AmPmInput {...getTimePeriodInputProps()} />}
        </div>
      </div>
    );
  },
);

TimePickerControl.displayName = 'TimePicker';

TimePickerControl.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** The initial time */
  initialTime: PropTypes.shape({
    hours: PropTypes.number.isRequired,
    minutes: PropTypes.number.isRequired,
  }).isRequired,
  /** Toggle error state */
  error: PropTypes.bool,
  /** Toggle disabled state */
  disabled: PropTypes.bool,
  /** callback when selected value changed */
  onChange: PropTypes.func,
  /** Override for 12hr or 24hr display.  Uses browser locale to determine this by default */
  is12Hour: PropTypes.bool,
};

export default TimePickerControl;
